import {Component, inject, Input, OnInit} from '@angular/core';
import {QueryDataService} from "../services/query-data.service";
import {DiffEditorComponent, DiffEditorModel, EditorComponent} from "ngx-monaco-editor-v2";
import {FormsModule} from "@angular/forms";
import {ActivatedRoute, NavigationExtras, Router} from "@angular/router";
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {NgOptimizedImage, UpperCasePipe} from "@angular/common";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {ConfirmationService} from "primeng/api";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {CardModule} from "primeng/card";
import {ButtonModule} from "primeng/button";
import {SelectModule} from "primeng/select";
import {InputTextModule} from "primeng/inputtext";
import {Popover} from "primeng/popover";

@Component({
    selector: 'app-query-detail',
    imports: [
        CardModule,
        EditorComponent,
        ButtonModule,
        FormsModule,
        DetailHeaderComponent,
        NgOptimizedImage,
        TranslatePipe,
        UpperCasePipe,
        DiffEditorComponent,
        SelectModule,
        InputTextModule,
        Popover,
        ConfigDetailComponent,
        DetailBodyComponent,
    ],
    templateUrl: './query-detail.component.html',
    styleUrl: './query-detail.component.scss'
})
export class QueryDetailComponent implements OnInit {
    @Input() navigateBack?: { commands: any[], extras?: NavigationExtras };

    private readonly queryDataService = inject(QueryDataService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly snackBar = inject(SnackbarService);
    private readonly translate = inject(TranslateService);
    private readonly config = inject(ConfigurationService).configuration;

    protected readonly ConfigRouteEnum = ConfigRouteEnum;

    subtitle: string | undefined;
    logoUrl: string | null = this.config.app.defaultLogo;
    id: number | undefined;
    appName: string = '';
    appVersion: string = '';
    queryName: string = '';
    queryVersion: string = '';
    dialect: string = '';
    allVersions: any[] = [];
    selectedFirstVersion?: any;
    selectedSecondVersion?: any;
    selectedSecondVersionId?: number;
    isComparisonActive: boolean = false;
    parameterInputs: { index: number; value: string }[] = [];
    editorOptions = {
        language: 'sql',
        readOnly: true,
        domReadOnly: true,
        automaticLayout: true,
        minimap: {enabled: false},
    };
    get code(): string {
        return this.renderSql(this.selectedFirstVersion?.query);
    }

    get versionOptions() {
        return [{id: this.id, version: this.queryVersion}, ...this.allVersions];
    }

    get compareOptions() {
        return [
            {id: undefined, version: this.translate.instant('query.compare')},
            ...this.allVersions.filter(v => v.id !== this.id),
        ];
    }

    get compareOptionsStop() {
        return [
            {id: undefined, version: this.translate.instant('query.stop')},
            ...this.allVersions.filter(v => v.id !== this.id),
        ];
    }

    options = {
        readOnly: true,
        automaticLayout: true,
        ignoreTrimWhitespace: false,
        originalEditable: false,
        minimap: {enabled: false},
    };
    originalModel: DiffEditorModel = {
        code: '',
        language: 'sql'
    };

    modifiedModel: DiffEditorModel = {
        code: '',
        language: 'sql'
    };

    ngOnInit() {
        this.activatedRoute.paramMap.subscribe(params => {
            const idFromUrl = params.get('id');
            if (idFromUrl) {
                this.id = Number(idFromUrl);
                this.loadQueryData();
            }
        });
    }

    private loadQueryData() {
        this.isComparisonActive = false;
        this.selectedSecondVersion = undefined;
        this.selectedSecondVersionId = undefined;
        this.queryDataService.getQueries().subscribe({
            next: (response: any) => {
                const queries = response._embedded?.query || [];
                const foundQuery = queries.find((query: any) => query.id === this.id);

                if (foundQuery) {
                    this.initializeParameterInputs(foundQuery.query);
                    this.appName = foundQuery.application.name;
                    this.appVersion = foundQuery.application.version;
                    this.queryName = foundQuery.name;
                    this.queryVersion = foundQuery.version;
                    this.dialect = foundQuery.dialect;

                    this.allVersions = queries
                        .filter((query: any) => query.name === foundQuery.name && query.id !== foundQuery.id)
                        .sort((a: any, b: any) => a.version.localeCompare(b.version, undefined, {numeric: true}));
                    this.selectedFirstVersion = foundQuery;
                    this.updateModelsInternal();
                }
            },
            error: (error) => console.error(error)
        });
    }

    secondVersion(queryId: number | undefined) {
        this.selectedSecondVersionId = queryId;
        const found = this.allVersions.find(version => version.id === queryId);
        this.selectedSecondVersion = found || undefined;
        this.isComparisonActive = !!this.selectedSecondVersion;
        this.updateModelsInternal();
    }

    protected updateModels() {
        this.updateModelsInternal();
    }

    private updateModelsInternal() {
        this.originalModel = {
            ...this.originalModel,
            code: this.renderSql(this.selectedFirstVersion?.query)
        };
        this.modifiedModel = {
            ...this.modifiedModel,
            code: this.renderSql(this.selectedSecondVersion?.query)
        };
    }

    deleteQuery() {
        this.confirmationService.confirm({
            header: this.translate.instant('button.delete'),
            message: this.translate.instant('confirmPopup.confirm'),
            acceptButtonProps: {label: this.translate.instant('button.confirm'), severity: 'danger'},
            rejectButtonProps: {label: this.translate.instant('button.cancel'), severity: 'secondary', variant: 'text'},
            accept: () => {
                this.queryDataService.deleteQuery(this.id).subscribe({
                    next: () => {
                        this.router.navigate(['/', ConfigRouteEnum.QUERY]);
                        this.snackBar.Success(this.translate.instant("snackbar.query.delete", {
                            queryName: this.queryName,
                            queryVersion: this.queryVersion
                        }));
                    },
                    error: (error) => {
                        console.error(error);
                        this.snackBar.Error(this.translate.instant("snackbar.query.delete_error"));
                    }
                });
            }
        });
    }

    editQuery() {
        this.router.navigate(['edit'], {relativeTo: this.activatedRoute});
    }

    createQuery() {
        this.router.navigate(['/', ConfigRouteEnum.QUERY, 'new']);
    }

    testQuery() {
        alert('backend not implemented yet');
        console.log('code:', this.code);
    }

    queryDetail(id: number) {
        this.router.navigate(['../', id], {relativeTo: this.activatedRoute});
    }

    stopComparing() {
        this.isComparisonActive = false;
        this.selectedSecondVersion = undefined;
        this.selectedSecondVersionId = undefined;
    }

    private initializeParameterInputs(sql: string) {
        const matches = [...(sql || '').matchAll(/\{\s*(\d+)\s*}/g)];
        const indexes = [...new Set(matches.map(m => Number(m[1])))].sort((a, b) => a - b);
        const existingValues = new Map(this.parameterInputs.map(p => [p.index, p.value]));

        this.parameterInputs = indexes.map(index => ({
            index,
            value: existingValues.get(index) ?? ''
        }));
    }

    private renderSql(sql: string): string {
        if (!sql) return '';
        let rendered = sql;
        this.parameterInputs.forEach(p => {
            if (p.value.trim()) {
                rendered = rendered.replaceAll(new RegExp(String.raw`\{\s*${p.index}\s*\}`, 'g'), p.value.trim());
            }
        });
        return rendered;
    }

}
